"use client";

import * as React from "react";
import "../styles/theme.css";
import "../styles/component.css";
import type { SkillsButtonProps } from "../types";
import { resolveEnvironment } from "../../../lib/environment";
import {
  resolveEnvValue,
  useRemoteConfigValue,
  type AppsConfig,
} from "../../../lib/remote-config";
import { TrackingBoundary } from "../../../lib/sentry/index";

/**
 * @deprecated 이동 대상은 apps.json(skills)이 단일 소스다. 이 상수는 더 이상
 * 폴백으로 쓰이지 않으며 하위 호환(공개 export)용으로만 남아 있다.
 */
export const DEFAULT_SKILLS_HREF = "https://www.onechain.nexus/skills";

export function SkillsButton({
  label = "Skills",
  href,
  onClick,
  className,
  style,
  theme,
  disabled = false,
  isLoading = false,
  loadingLabel = "Loading...",
  openInNewTab = false,
  type = "button",
}: SkillsButtonProps) {
  const [loading, setLoading] = React.useState(false);

  const env = resolveEnvironment();
  const remoteHref = useRemoteConfigValue<AppsConfig, string>(
    "apps.json",
    env,
    (config) => resolveEnvValue(config?.skills, env),
  );
  // 우선순위: 명시 href > apps.json 원격 값. 원격 로드 전 클릭은 no-op.
  const resolvedHref = href ?? remoteHref;

  const handleClick = async (_e: React.MouseEvent<HTMLButtonElement>) => {
    if (resolvedHref && !onClick) {
      if (openInNewTab) {
        window.open(resolvedHref, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = resolvedHref;
      }
      return;
    }

    if (onClick) {
      try {
        setLoading(true);
        await onClick();
      } finally {
        setLoading(false);
      }
    }
  };

  const isDisabled = disabled || isLoading || loading;
  const showLoading = isLoading || loading;
  const displayLabel = showLoading ? loadingLabel : label;
  const classes = className ? `sb-button ${className}` : "sb-button";

  return (
    <TrackingBoundary feature="skills">
      <button
        type={type}
        onClick={handleClick}
        disabled={isDisabled}
        className={classes}
        data-theme={theme}
        data-track="click"
        style={style}
        aria-busy={showLoading}
      >
        {showLoading ? (
          <span className="sb-spinner" aria-hidden="true" />
        ) : (
          <SparklesIcon />
        )}
        <span className="sb-label">{displayLabel}</span>
      </button>
    </TrackingBoundary>
  );
}

function SparklesIcon() {
  return (
    <svg
      className="sb-button-icon"
      viewBox="0 0 34 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M15.5879 3.95142C16.0211 2.78097 17.6761 2.78099 18.1094 3.95142L19.6221 8.03833C20.6569 10.8348 22.8618 13.0396 25.6582 14.0745L29.7451 15.5872C30.9157 16.0204 30.9157 17.6754 29.7451 18.1086L25.6582 19.6213C22.8618 20.6562 20.657 22.861 19.6221 25.6575L18.1094 29.7444C17.6762 30.9149 16.0211 30.9149 15.5879 29.7444L14.0752 25.6575C13.0403 22.861 10.8355 20.6562 8.03906 19.6213L3.95215 18.1086C2.78172 17.6754 2.7817 16.0204 3.95215 15.5872L8.03906 14.0745C10.8355 13.0396 13.0403 10.8348 14.0752 8.03833L15.5879 3.95142Z"
        stroke="currentColor"
        strokeWidth="1.93556"
      />
    </svg>
  );
}
